import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import { Security } from '@okta/okta-react'
import { observer } from 'mobx-react'
import { lazy, createContext, Suspense, useContext, useEffect, useState } from 'react'
// TODO: Stop using react-lazily
import { lazily } from 'react-lazily'
import GlobalStyle from './styles/globalCss'
import { LoginCallback } from './components/Login/LoginCallback'
import { Navbar } from './components/Navbar/Navbar'
import { oktaAuth } from './oktaAuth'
import {
  appSectionsRouteGroup,
  checklistReportsLink,
  checklistsRouteGroup,
  loginCallbackLink,
  loginLink,
  mainNavigationMenuButtonGroups,
  missionHistoryLink,
  flightNetworkLink,
  underConstructionRoutesGroup,
  useRoute,
  usersRouteGroup,
  vehicleRouteGroup,
  WithAppSectionRoute,
  partNumbersRouteGroup,
  discrepancyReportsRouteGroup,
  centerSettingsRouteGroup,
  logoutLink,
} from './router'

import type { WithNotificationStore, WithRootStore } from './stores/RootStoreModel'
import './App.css';
import { Main } from './components/Page/Page'
import { SpinnerIcon } from './components/Spinner/Spinner'
import { SnackBar, SnackBarType } from './components/SnackBar/SnackBar'
import { NotificationLevel } from './stores/NotificationStore'
import styled from 'styled-components'
import FetchingWrapper from './components/Page/FetchingWrapper'
import { autorun } from 'mobx'
import { VehiclesPage } from './pages/VehiclesPage'
import { Dialog, DialogProps } from './components/Dialog/Dialog'
import { SentryService } from './utils/SentryService'
import ErrorBoundary from 'components/ErrorBoundary'
import CredentialsContext from 'contexts/Credentials'

const { ChecklistReportsPage } = lazily(
  () => import(/* webpackChunkName: 'ChecklistReportsPage' */ './pages/ChecklistReportsPage'),
)
const { CenterSettingsPage } = lazily(
  () => import(/* webpackChunkName: 'CenterSettingsPage' */ './pages/CenterSettingsPage'),
)
const { UsersPage } = lazily(() => import(/* webpackChunkName: 'UserPage' */ './pages/UsersPage'))

const { UnderConstructionPage } = lazily(
  () => import(/* webpackChunkName: 'UnderConstructionPage' */ './pages/UnderConstructionPage'),
)

const { LoginPage } = lazily(
  () => import(/* webpackChunkName: 'LoginPage' */ './pages/LoginPage'),
)

const { LogoutPage } = lazily(
  () => import(/* webpackChunkName: 'LogoutPage' */ './pages/LogoutPage'),
)

const { ChecklistsPage } = lazily(
  () => import(/* webpackChunkName: 'ChecklistsPage' */ './pages/ChecklistsPage'),
)

const MissionHistoryPage = lazy(
  () => import(/* webpackChunkName: 'MissionHistoryPage' */ './pages/MissionHistoryPage'),
)

const FlightNetworkPage = lazy(
  () => import(/* webpackChunkName: 'FlightNetworkPage' */ './pages/FlightNetworkPage')
);

const { PartNumbersPage } = lazily(
  () => import(/* webpackChunkName: 'PartNumbersPage' */ './pages/PartNumbersPage'),
)

const { DiscrepancyReportsPage } = lazily(
  () => import(/* webpackChunkName: 'DiscrepancyReportsPage' */ './pages/DiscrepancyReportsPage'),
)

interface AppProps extends WithRootStore {}

// This method isn't really being used right now, since we don't use react Router and the <SecureRoute /> tag.
// But we do have it here since it's required by <Security> tag, and we plan to add using it later on.
const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri: string) => {
  window.location.replace(toRelativeUrl(originalUri || '/', window.location.origin))
}

// Mission history is homepage, decided by product team.
const homePageLink = missionHistoryLink

export const App = observer(({ store }: AppProps) => {
  const [credentials, setCredentials] = useState({})

  const currentRoute = useRoute()

  useEffect(() => {
    if (currentRoute.name === 'home') {
      homePageLink.replace()
    }
  }, [currentRoute])

  const { name: routeName } = currentRoute
  // We don't yet use <Security/>, and [restoreOriginalUri] as described it okta docs, since we don't use react routing.
  // Nevertheless We add this to use [useOktaAuth] in the children components, and to prepare switching to React Routing later.
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <GlobalStyle />
      <CenterAppPageWithNotifications {...{ ...store }}>
        <CredentialsContext.Provider value={{ credentials, setCredentials }}>
          <ErrorBoundary>
            {routeName === false && <div>404</div>}
            {routeName === loginLink.name && <LoginPage {...{ loginType: 'okta', ...store }} />}
            {routeName === loginCallbackLink.name && <LoginCallback {...{ currentRoute, ...store }} />}
            {appSectionsRouteGroup.has(currentRoute) && <CenterAppSectionPageOrSpinner {...{ currentRoute, store }} />}
          </ErrorBoundary>
        </CredentialsContext.Provider>
      </CenterAppPageWithNotifications>
    </Security>
  )
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ShowDialogContext = createContext((dialog: DialogProps | null) => { console.log('No dialog provider') })
export function useShowDialog() {
  return useContext(ShowDialogContext)
}

interface CenterAppPageWithNotificationsProps extends WithNotificationStore, React.PropsWithChildren { }
const CenterAppPageWithNotifications = observer(({ notificationStore, children }: CenterAppPageWithNotificationsProps) => {
  const { notifications } = notificationStore
  const [dialog, showDialog] = useState<DialogProps | null>(null)

  return (
    <Main>
      <ShowDialogContext.Provider value={showDialog}>
        {dialog && <Dialog {... { ...dialog }} />}
        {children}
      </ShowDialogContext.Provider>
      <Notifications>
        {notifications.map(({ message, level }, index) =>
          (
            <SnackBar {...{
              key: index,
              message,
              type: getSnackbarType(level)
            }} />
          )
        )}
      </Notifications>
    </Main>
  )
})

interface CenterAppSectionPageOrSpinnerProps extends WithRootStore, WithAppSectionRoute { }

// A wrapper for the actual center app page which makes sure that the app commonSettings are initialized 
// before displaying any application page.
// TODO: Also add routing to login page if user not logged in here (currently done in FetchModel when receiving 401 from backend, not very tidy)
const CenterAppSectionPageOrSpinner = observer(({ store, currentRoute }: CenterAppSectionPageOrSpinnerProps) => {
  const { name: routeName } = currentRoute
  const fetchCommonSettingsStarted = store.commonSettingsStore.fetchCommonSettingsStatus.isStarted
  const fetchCommonSettingsSucceeded = store.commonSettingsStore.fetchCommonSettingsStatus.isSuccess
  useEffect(() => {
    if (!fetchCommonSettingsStarted) {
      void store.commonSettingsStore.initializeCommonSettings().then(() => {
        if (store.commonSettingsStore.currentUserAndPermissions !== null) {
          SentryService.setUser(store.commonSettingsStore.currentUserAndPermissions.user_data)
        } 
      })
      autorun(() => {
        // Whenever centerSettingsStore is updated, make sure to update commonSettingsStore (which also updates MeasurementUnitsService)
        const newMeasurementUnit = store.centerSettingsStore.centerSettings?.deploymentSettings.measurement_units
        const newMaxDistanceToLandingM = store.centerSettingsStore.centerSettings?.deploymentSettings.max_distance_to_landing_m

        if (newMeasurementUnit !== undefined) {
          store.commonSettingsStore.setMeasurementUnits(newMeasurementUnit)
        }

        if (newMaxDistanceToLandingM !== undefined) {
          store.commonSettingsStore.setMaxDistanceToLandingM(newMaxDistanceToLandingM)
        }
      })
    }
    if (fetchCommonSettingsSucceeded) {
      const centerId = store.commonSettingsStore.centerId
      if (centerId === undefined) {
        throw Error('CenterId should be known after fetched common settings!')
      }
    }
  }, [store.commonSettingsStore.fetchCommonSettingsStatus])

  return (
    <FetchingWrapper {...{
      fetchStatus: store.commonSettingsStore.fetchCommonSettingsStatus,
      inProgressTitle: 'Initializing center app',
      errorMessagePrefix: 'Oops... Center app failed to initialize',
      onRetry: store.commonSettingsStore.initializeCommonSettings
    }}>
      <Navbar {...{ linkGroups: mainNavigationMenuButtonGroups, currentRoute, ...store }} />
      <Suspense fallback={<SpinnerIcon title="Loading page..." />}>
        {routeName === logoutLink.name && <LogoutPage {...{ currentRoute, ...store }} />}
        {centerSettingsRouteGroup.has(currentRoute) && <CenterSettingsPage {...{ currentRoute, ...store }} />}
        {routeName === checklistReportsLink.name && <ChecklistReportsPage {...{ ...store }} />}
        {usersRouteGroup.has(currentRoute) && <UsersPage {...{ currentRoute, ...store }} />}
        {checklistsRouteGroup.has(currentRoute) && <ChecklistsPage {...{ currentRoute, ...store }} />}
        {routeName === missionHistoryLink.name && <MissionHistoryPage {...{ ...store }} />}
        {routeName === flightNetworkLink.name && <FlightNetworkPage {...{ currentRoute, ...store }} />}
        {partNumbersRouteGroup.has(currentRoute) && <PartNumbersPage {...{ currentRoute, ...store }} />}
        {vehicleRouteGroup.has(currentRoute) && <VehiclesPage {...{ currentRoute, ...store }} />}
        {discrepancyReportsRouteGroup.has(currentRoute) && <DiscrepancyReportsPage {...{ currentRoute, ...store }} />}
        {underConstructionRoutesGroup.has(currentRoute) && <UnderConstructionPage />}
      </Suspense>
    </FetchingWrapper>)
})

const Notifications = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 1em;
  inset-inline-end: 1em;
  inset-block-end: 1em;
  align-items: flex-end;
`

function getSnackbarType(level: NotificationLevel): SnackBarType {
  switch (level) {
    case NotificationLevel.Error:
      return SnackBarType.Error
    case NotificationLevel.Warning:
      return SnackBarType.Warning
    case NotificationLevel.Info:
      return SnackBarType.Info
    case NotificationLevel.Success:
      return SnackBarType.Success
  }
}